import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FocusTrap } from "@/components/a11y/FocusTrap";
import { useAuthStore } from "@/stores/authStore";
import { useMarketStore } from "@/stores/marketStore";
import { useChatStore } from "@/stores/chatStore";
import { useMapStore } from "@/stores/mapStore";
import type { Market } from "@/config/markets";
import { UpgradeModal } from "@/features/upgrade/UpgradeModal";

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk" },
] as const;

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const { logout, user } = useAuthStore();
  const { market, setMarket } = useMarketStore();
  const [confirmClear, setConfirmClear] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [sendOnEnter, setSendOnEnter] = useState(() =>
    localStorage.getItem("ortvis-send-on-enter") !== "false"
  );

  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark"
  );

  function toggleTheme() {
    const html = document.documentElement;
    const next = html.getAttribute("data-theme") === "dark" ? "light" : "dark";
    html.setAttribute("data-theme", next);
    localStorage.setItem("ortvis-theme", next);
    setIsDark(next === "dark");
  }

  function handleLanguageChange(code: string) {
    i18n.changeLanguage(code);
  }

  function handleClearHistory() {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    useChatStore.getState().resetChat();
    useChatStore.getState().setConversations([]);
    useMapStore.getState().clearFeatures();
    setConfirmClear(false);
    onClose();
  }

  function handleLogout() {
    onClose();
    logout();
  }

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  // Focus dialog and reset confirm state on open
  useEffect(() => {
    if (open) {
      dialogRef.current?.focus();
      setConfirmClear(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <FocusTrap>
    <div className="cx-shortcuts-backdrop animate-cx-fade-in" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-label={t("settings.title")}
        tabIndex={-1}
        className="cx-settings-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="cx-dialog-header flex items-center justify-between px-5 py-4">
          <h2 className="cx-dialog-title text-[15px] font-medium">
            {t("settings.title")}
          </h2>
          <button onClick={onClose} className="cx-msg-action" aria-label={t("common.close")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-2">
          {/* ── Appearance ── */}
          <div className="cx-setting-section-header">{t("settings.appearance")}</div>

          <div className="cx-setting-row">
            <div className="cx-setting-label">
              <span>{t("settings.theme")}</span>
              <span className="cx-setting-desc">{t("settings.themeDesc")}</span>
            </div>
            <div className="cx-toggle-group">
              <button
                className={`cx-toggle-btn${!isDark ? " cx-toggle-btn--active" : ""}`}
                onClick={() => { if (isDark) toggleTheme(); }}
              >
                {t("settings.light")}
              </button>
              <button
                className={`cx-toggle-btn${isDark ? " cx-toggle-btn--active" : ""}`}
                onClick={() => { if (!isDark) toggleTheme(); }}
              >
                {t("settings.dark")}
              </button>
            </div>
          </div>

          <div className="cx-setting-row">
            <div className="cx-setting-label">
              <span>{t("settings.language")}</span>
              <span className="cx-setting-desc">{t("settings.languageDesc")}</span>
            </div>
            <select
              className="cx-select"
              value={i18n.language}
              onChange={(e) => handleLanguageChange(e.target.value)}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
          </div>

          <div className="cx-setting-row">
            <div className="cx-setting-label">
              <span>{t("settings.market")}</span>
              <span className="cx-setting-desc">{t("settings.marketDesc")}</span>
            </div>
            <div className="cx-toggle-group">
              {(["SE", "NO"] as Market[]).map((m) => (
                <button
                  key={m}
                  className={`cx-toggle-btn${market === m ? " cx-toggle-btn--active" : ""}`}
                  onClick={() => setMarket(m)}
                >
                  {t(`market.${m}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="cx-setting-row">
            <div className="cx-setting-label">
              <span>{t("settings.sendOnEnter")}</span>
              <span className="cx-setting-desc">{t("settings.sendOnEnterDesc")}</span>
            </div>
            <div className="cx-toggle-group">
              <button
                className={`cx-toggle-btn${sendOnEnter ? " cx-toggle-btn--active" : ""}`}
                onClick={() => { setSendOnEnter(true); localStorage.setItem("ortvis-send-on-enter", "true"); }}
              >
                Enter
              </button>
              <button
                className={`cx-toggle-btn${!sendOnEnter ? " cx-toggle-btn--active" : ""}`}
                onClick={() => { setSendOnEnter(false); localStorage.setItem("ortvis-send-on-enter", "false"); }}
              >
                ⇧ Enter
              </button>
            </div>
          </div>

          {/* ── Plan ── */}
          <div className="cx-setting-section-header">{t("upgrade.yourPlan")}</div>

          <div className="cx-setting-row">
            <div className="cx-setting-label">
              <span>{t(`upgrade.tierName.${user?.tier ?? "free"}`)}</span>
              <span className="cx-setting-desc">{t("upgrade.yourPlanDesc")}</span>
            </div>
            <button className="cx-toggle-btn" onClick={() => setUpgradeOpen(true)}>
              {t("upgrade.viewPlans")}
            </button>
          </div>

          {/* ── Data ── */}
          <div className="cx-setting-section-header">{t("settings.data")}</div>

          <div className="cx-setting-row">
            <div className="cx-setting-label">
              <span>{t("settings.clearHistory")}</span>
              <span className="cx-setting-desc">{t("settings.clearHistoryDesc")}</span>
            </div>
            <button
              className={`cx-toggle-btn${confirmClear ? " cx-toggle-btn--danger" : ""}`}
              onClick={handleClearHistory}
            >
              {confirmClear ? t("settings.confirmClear") : t("settings.clear")}
            </button>
          </div>

          <div className="cx-setting-row cx-setting-row--last">
            <div className="cx-setting-label">
              <span>{t("auth.logout")}</span>
              <span className="cx-setting-desc">{t("settings.logoutDesc")}</span>
            </div>
            <button className="cx-toggle-btn" onClick={handleLogout}>
              {t("auth.logout")}
            </button>
          </div>
        </div>
      </div>
    </div>
    <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </FocusTrap>
  );
}
